import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../../theme";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import BookIcon from "@mui/icons-material/Book";
import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../../../../../../components/ui/Button";
import { DownloadIcon } from "../../../../../../components/icons";
import { PrintIcon } from "../../../../../../components/icons/PrintIcon";
import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import { MemoryBook } from "../../../../../../types/memory-table";
import * as pdfjsLib from "pdfjs-dist";
import { DeleteOutline, VisibilityOutlined } from "@mui/icons-material";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const generateCoverFromPdf = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({
    canvasContext: canvas.getContext("2d")!,
    viewport,
    canvas,
  }).promise;
  return canvas.toDataURL("image/png");
};

interface TributeBookTabProps {
  streamId: number | null;
  deceasedName?: string;
}

export default function TributeBookTab({
  streamId,
  deceasedName,
}: TributeBookTabProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [canvaLink, setCanvaLink] = useState("");
  const [notes, setNotes] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [selectedBook, setSelectedBook] = useState<File | null>(null);
  const [memoryBooks, setMemoryBooks] = useState<MemoryBook[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentBook = memoryBooks[0] ?? null;

  const fetchMemoryBooks = useCallback(async () => {
    if (!streamId) return;
    setIsLoading(true);
    setError(false);
    try {
      const response = await apiService.get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${streamId}`,
      );
      setMemoryBooks(response.data.results || []);
    } catch {
      setError(true);
      showErrorToast("Failed to load memory books.");
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    fetchMemoryBooks();
  }, [fetchMemoryBooks]);

  useEffect(() => {
    if (memoryBooks.length > 0) {
      setCanvaLink(memoryBooks[0].canva_memory_book_link || "");
      setNotes(memoryBooks[0].notes || "");
    }
  }, [memoryBooks]);

  const handleCopyLink = () => {
    if (canvaLink) navigator.clipboard.writeText(canvaLink);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type === "application/pdf") {
      setSelectedBook(file);
      generateCoverFromPdf(file).then(setCoverPreview);
    } else {
      showErrorToast("Please drop a PDF file.");
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedBook(file);
      const cover = await generateCoverFromPdf(file);
      setCoverPreview(cover);
    }
  };

  const handleUpload = async () => {
    if (!selectedBook || !streamId) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", selectedBook);
    formData.append("stream_id", `${streamId}`);
    formData.append("canva_memory_book_link", canvaLink);
    formData.append("notes", notes || "No notes");
    try {
      await apiService.post(`/api/streams/memory-book/`, formData);
      showSuccessToast(`${selectedBook.name} uploaded successfully!`);
      setSelectedBook(null);
      setCoverPreview(null);
      fetchMemoryBooks();
    } catch {
      showErrorToast("Failed to upload book. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!currentBook) return;
    setIsSaving(true);
    const formData = new FormData();
    formData.append("canva_memory_book_link", canvaLink);
    formData.append("notes", notes);
    try {
      await apiService.patch(
        `/api/streams/memory-book/${currentBook.id}/`,
        formData,
      );
      showSuccessToast("Details updated successfully.");
      fetchMemoryBooks();
    } catch {
      showErrorToast("Failed to update details.");
    } finally {
      setIsSaving(false);
    }
  };
  const handleDownload = () => {
    if (!currentBook) return;
    if (!currentBook.file) {
      showErrorToast("No file has been uploaded for this memory book yet.");
      return;
    }
    fetch(currentBook.file)
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `memory_book_${currentBook.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      })
      .catch(() => showErrorToast("Failed to download memory book."));
  };

  const handleDeleteBook = async (id: number) => {
    try {
      await apiService.delete(`/api/streams/memory-book/${id}/`);
      showSuccessToast("Memory book deleted.");
      fetchMemoryBooks();
    } catch {
      showErrorToast("Failed to delete memory book.");
    }
  };

  const handleOpen = () => {
    if (!currentBook) return;
    if (!currentBook.file) {
      showErrorToast("No file has been uploaded for this memory book yet.");
      return;
    }
    window.open(currentBook.file, "_blank", "noopener,noreferrer");
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const getUploadedBy = (book: MemoryBook) => {
    if (!book.created_by) return "Unknown";
    const { first_name, last_name, username } = book.created_by;
    if (first_name && last_name) return `${first_name} ${last_name}`;
    return username || "Unknown";
  };

  return (
    <Box mt={3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={3}
        alignItems={{ xs: "center", sm: "flex-start" }}
        mb={3}
        sx={{ pl: { xs: 0, sm: 3 } }}
      >
        <Box
          sx={{
            width: { xs: "55%", sm: 200 },
            maxWidth: 200,
            flexShrink: 0,
            borderRadius: 2,
            overflow: "hidden",
            aspectRatio: "3/4",
          }}
        >
          {coverPreview ? (
            <Box
              component="img"
              onClick={handleOpen}
              src={coverPreview}
              sx={{ width: "100%", display: "block", cursor: "pointer" }}
            />
          ) : currentBook?.file ? (
            <Box
              onClick={handleOpen}
              sx={{ width: "100%", height: "100%", cursor: "pointer" }}
            >
              <iframe
                src={`${currentBook.file}#toolbar=0&navpanes=0&scrollbar=0`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  pointerEvents: "none",
                }}
                title="Memory Book Cover"
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "text.secondary",
                  textAlign: "center",
                  px: 1,
                }}
              >
                No cover yet
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ width: "100%", textAlign: { xs: "center", sm: "left" } }}>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#724E41",
              fontWeight: 500,
              mb: 0.5,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Memory Book of
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "24px", sm: "30px", md: "36px" },
              fontWeight: 700,
              color: "#724E41",
              mb: 2.5,
              lineHeight: 1.2,
            }}
          >
            {deceasedName}
          </Typography>

          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1.5}
            alignItems="center"
            justifyContent={{ xs: "center", sm: "flex-start" }}
          >
            <Button
              variant="contained"
              sx={{ textTransform: "none", fontSize: "13px", height: "36px" }}
            >
              Share
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#f3e4dA",
                color: "#724e41",
                textTransform: "none",
                boxShadow: "none",
                fontSize: "13px",
                height: "36px",
              }}
            >
              Notify Contributors
            </Button>
            <IconButton
              size="small"
              disabled={!currentBook}
              onClick={handleDownload}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "12px",
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              <DownloadIcon sx={{ color: "#724e41", fontSize: 22 }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={!currentBook}
              onClick={handleOpen}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              <PrintIcon sx={{ color: "#724e41", fontSize: 22 }} />
            </IconButton>
          </Stack>
        </Box>
      </Stack>

      <Paper
        sx={{
          borderRadius: 4,
          p: { xs: 2, sm: 3 },
          boxShadow: 0,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              width: { xs: "100%", sm: 240 },
              flexShrink: 0,
              bgcolor: colors.primary[900],
              border: `3px dashed ${isDragging ? "#724e41" : "#b2bac2"}`,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 3, sm: 4 },
              cursor: "pointer",
              transition: "all 0.2s",
              minHeight: { xs: 140, sm: "auto" },
              "&:hover": { borderColor: "#724e41", bgcolor: "#fdf6f4" },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              {selectedBook ? (
                <BookIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              ) : (
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: 20, color: "text.secondary" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                textAlign: "center",
                mb: 0.5,
              }}
            >
              {selectedBook
                ? selectedBook.name
                : "Drag and drop your PDF here (max 25MB)"}
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 1.5, bgcolor: "#8e8e8e", color: "#e8e8e8" }}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Browse files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "text.primary",
                mb: 0.75,
              }}
            >
              Canva Link
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={canvaLink}
              onChange={(e) => setCanvaLink(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleCopyLink}
                      edge="end"
                    >
                      <ContentCopyIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "13px",
                  bgcolor: colors.primary[900],
                  "& fieldset": { border: "none" },
                },
              }}
            />

            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "text.primary",
                mb: 0.75,
              }}
            >
              Notes
            </Typography>
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "13px",
                    bgcolor: colors.primary[900],
                    "& fieldset": { border: "none" },
                  },
                }}
              />
              <Button
                variant="contained"
                disabled={isUploading || isSaving}
                onClick={selectedBook ? handleUpload : handleUpdate}
                sx={{
                  position: "absolute",
                  right: "0.7rem",
                  bottom: "0.7rem",
                }}
              >
                {isUploading || isSaving ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : selectedBook ? (
                  "Upload"
                ) : (
                  "Update"
                )}
              </Button>
            </Box>
          </Box>
        </Stack>

        <Paper
          sx={{ borderRadius: "14px", boxShadow: 0, overflow: "hidden", mt: 2 }}
        >
          {/* Table Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 80px",
                sm: "1fr 1fr 80px",
                md: "1fr 1fr 1fr 80px",
              },
              bgcolor: "background.paper",
              px: 2,
              py: 1.2,
              borderBottom: "1px solid #f0f4f8",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: 0.5,
              }}
            >
              FILE NAME
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: 0.5,
                display: { xs: "none", md: "block" },
              }}
            >
              UPLOADED BY
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: 0.5,
                display: { xs: "none", sm: "block" },
              }}
            >
              DATE
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: 0.5,
                textAlign: "center",
              }}
            >
              ACTIONS
            </Typography>
          </Box>

          {/* Table Body */}
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography sx={{ fontSize: "12px", color: "#ef4444", mb: 1 }}>
                Failed to load upload history
              </Typography>
              <Button variant="contained" onClick={fetchMemoryBooks}>
                Retry
              </Button>
            </Box>
          ) : memoryBooks.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: "12px", color: "#cbd5e1" }}>
                No upload history yet
              </Typography>
            </Box>
          ) : (
            memoryBooks.map((book, index) => (
              <Box
                key={book.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 80px",
                    sm: "1fr 1fr 80px",
                    md: "1fr 1fr 1fr 80px",
                  },
                  px: 2,
                  py: 1.5,
                  alignItems: "center",
                  borderBottom:
                    index < memoryBooks.length - 1
                      ? "1px solid #f0f4f8"
                      : "none",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#475569",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    pr: 1,
                  }}
                >
                  {book.file ? book.file.split("/").at(-1) : "No file uploaded"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#475569",
                    display: { xs: "none", md: "block" }, // hidden on mobile
                  }}
                >
                  {getUploadedBy(book)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#475569",
                    display: { xs: "none", sm: "block" }, // hidden on mobile only
                  }}
                >
                  {formatDate(book.created_at)}
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}
                >
                  <IconButton
                    size="small"
                    disabled={!book.file}
                    onClick={() => {
                      if (book.file) {
                        window.open(book.file, "_blank", "noopener,noreferrer");
                      }
                    }}
                  >
                    <VisibilityOutlined
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteBook(book.id)}
                  >
                    <DeleteOutline sx={{ fontSize: 16, color: "#ef4444" }} />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Paper>
      </Paper>
    </Box>
  );
}
